export const STALE_TIME = 1000 * 60 * 60 * 1; // 1 hour

export enum QueryId {
  AVAILABLE_OFFERS = 'available-offers',
  AVAILABLE_OFFERS_BEST_OFFERS = 'available-offers-best-offers',
  BOOKING_PRICE_DETAILS = 'booking-price-details',
  CAMPAIGNS_AVAILABLE_DATES = 'campaigns-available-dates',
  CAMPAIGNS_AVAILABLE_OFFERS = 'campaigns-available-offers',
  CAMPAIGNS_DESTINATION_HIERARCHY = 'campaigns-destination-hierarchy',
  CAMPAIGNS_HOTELS_WITH_OFFERS = 'campaigns-hotels-with-offers',
  CANCELLATION_INSURANCE = 'cancellation-insurance',
  CONTENT_SEARCH = 'content-search',
  COUNTRIES_MAP = 'countries-map',
  CROSS_BRAND_CUSTOM_PRODUCT_LIST = 'cross-brand-custom-product-list',
  CUSTOMER_SUMMARY = 'customer-summary',
  DEPARTURES = 'departures',
  DEPARTURES_AVAILABLE_DATES = 'departures-available-dates',
  DEPARTURE_SELECTOR = 'departure-selector',
  DESTINATIONS_MAP = 'destinations-map',
  DESTINATION_HIERARCHY = 'destination-hierarchy',
  DETAILED_CUSTOMER = 'detailed-customer',
  DETAILED_PASSENGER = 'detailed-passenger',
  DISCOUNT_CODE = 'discount-code',
  FAVORITES_HOTELS_WITH_OFFERS = 'favorites-hotels-with-offers',
  FILTERS = 'filters',
  FLIGHT_SELECTOR = 'flight-selector',
  FLIGHT_SUMMARY = 'flight-summary',
  GENERIC_PRODUCT = 'generic-product',
  GIFT_CARD = 'gift-card',
  GOLF_PACKAGE = 'golf-package',
  HOTELS_PICTURES = 'hotels-pictures',
  HOTELS_WITH_OFFERS = 'hotels-with-offers',
  HOTELS_WITH_OFFERS_BEST_OFFERS = 'hotels-with-offers-best-offers',
  HOTEL_SELECTOR = 'hotel-selector',
  HOTEL_SUMMARY = 'hotel-summary',
  MODAL = 'modal',
  MULTI_GENERIC_PRODUCT = 'multi-generic-product',
  MULTI_SELECT_GENERIC_PRODUCT = 'multi-select-generic-product',
  MY_TRIP_BOOKING_OVERVIEW = 'my-trip-booking-overview',
  MY_TRIP_PASSENGER_SUMMARY = 'my-trip-passenger-summary',
  MY_TRIP_PAYMENT_RECEIPT = 'my-trip-payment-receipt',
  MY_TRIP_PRODUCT_CAROUSEL = 'my-trip-product-carousel',
  MY_TRIP_SURVEY = 'my-trip-survey',
  MY_TRIP_UPCOMING_PAYMENT = 'my-trip-upcoming-payment',
  MY_TRIP_WISHLIST = 'my-trip-wishlist',
  ONBOARDING_FLOW_STEP_HEADER = 'onboarding-flow-step-header',
  PARENT_PRODUCT = 'parent-product',
  PASSENGER_SUMMARY = 'passenger-summary',
  PAY_WITH_GIFT_CARD = 'pay-with-gift-card',
  PICKUP_POINT = 'pickup-point',
  PRE_BOOKING_PAYMENT = 'pre-booking-payment',
  PRODUCT_SELECTOR = 'product-selector',
  PROFILE_COMPANIONS = 'profile-companions',
  PROFILE_ENTRY_CARD = 'profile-entry-card',
  PROFILE_HEADER = 'profile-header',
  PROFILE_LOYALTY_BENEFITS = 'profile-loyalty-benefits',
  PROFILE_LOYALTY_ENTRY = 'profile-loyalty-entry',
  PROFILE_NO_LOYALTY_WALL = 'profile-no-loyalty-wall',
  PROFILE_PAST_TRIPS = 'profile-past-trips',
  PROFILE_TRIP_INVITATION = 'profile-trip-invitation',
  PROFILE_UPCOMING_TRIPS = 'profile-upcoming-trips',
  PROFILE_USER_CONSENTS = 'profile-user-consents',
  PROFILE_USER_DELETE = 'profile-user-delete',
  PROFILE_USER_HEADER = 'profile-user-header',
  PROFILE_USER_INFORMATION = 'profile-user-information',
  RETRIEVE_SHARED_FAVORITES = 'retrieve-shared-favorites',
  ROOM_MATRIX = 'room-matrix',
  ROOM_MATRIX_SELECTOR = 'room-matrix-selector',
  ROOM_SELECTION = 'room-selection',
  SEARCH_ADDRESS = 'search-address',
  SEARCH_AVAILABLE_DATES = 'search-available-dates',
  SEARCH_DESTINATION_HIERARCHY = 'search-destination-hierarchy',
  SEARCH_SETTINGS = 'search-settings',
  SEARCH_SINGLE = 'search-single',
  SEAT_SELECTION = 'seat-selection',
  SIMPLE_CUSTOMER = 'simple-customer',
  SIMPLE_PASSENGER = 'simple-passenger',
  STORE_SHARED_FAVORITES = 'store-shared-favorites',
  TEE_TIME = 'tee-time',
  TEE_TIMES = 'tee-times',
  TERMS_AND_CONDITIONS = 'terms-and-conditions',
  TRANSPORTS_SELECTOR = 'transports-selector',
  TRAVELER_ATTRIBUTES = 'traveler-attributes',
  TRAVEL_INSURANCE = 'travel-insurance',
  TRAVEL_PLAN = 'travel-plan',
  TRAVEL_SUMMARY = 'travel-summary',
  TRIP_CHECKLIST = 'trip-checklist',
  VALIDATE_GIFT_CARD = 'validate-gift-card',
}

/**
 * Queries that depend on customer/passenger age.
 *
 * Updating the age of the customer or a passenger can affect product
 * eligibility or pricing. These queries should be invalidated and
 * refetched whenever the age changes for either the customer or a passenger.
 */
export const AGE_DEPENDENT_QUERIES = [
  QueryId.CANCELLATION_INSURANCE,
  QueryId.GENERIC_PRODUCT,
  QueryId.MULTI_GENERIC_PRODUCT,
  QueryId.MULTI_SELECT_GENERIC_PRODUCT,
  QueryId.PARENT_PRODUCT,
  QueryId.PICKUP_POINT,
  QueryId.TEE_TIME,
  QueryId.TRAVEL_INSURANCE,
];

/**
 * Queries that depend on customer/passenger names.
 *
 * If a customer or passenger name changes, these queries should be invalidated
 * and refetched, since the names are displayed on the associated places.
 */
export const NAME_DEPENDENT_QUERIES = [
  QueryId.CANCELLATION_INSURANCE,
  QueryId.CUSTOMER_SUMMARY,
  QueryId.GENERIC_PRODUCT,
  QueryId.GOLF_PACKAGE,
  QueryId.HOTEL_SUMMARY,
  QueryId.MULTI_GENERIC_PRODUCT,
  QueryId.MULTI_SELECT_GENERIC_PRODUCT,
  QueryId.PARENT_PRODUCT,
  QueryId.PASSENGER_SUMMARY,
  QueryId.PICKUP_POINT,
  QueryId.ROOM_SELECTION,
  QueryId.TEE_TIME,
  QueryId.SEAT_SELECTION,
  QueryId.TRAVEL_INSURANCE,
];

/**
 * Queries that depend on the booking's total price.
 *
 * Any operation that adds or removes a product, or in any way changes the total
 * price of the booking, should invalidate these queries so the updated total price
 * is reflected throughout the booking.
 */
export const TOTAL_PRICE_DEPENDENT_QUERIES = [
  QueryId.BOOKING_PRICE_DETAILS,
  QueryId.PASSENGER_SUMMARY,
  QueryId.TRAVEL_SUMMARY,
];

/**
 * Queries that should be invalidated when the customer or a passenger's date of
 * birth changes.
 *
 * A date of birth change triggers the age-dependent queries to be refetched.
 * After refetching, a customer or passenger may no longer be eligible for an
 * already selected product and could be removed from it, which may change the
 * booking's total price. Therefore, the total price queries also need to be
 * invalidated.
 */
export const DATE_OF_BIRTH_DEPENDENT_QUERIES = [
  ...AGE_DEPENDENT_QUERIES,
  ...TOTAL_PRICE_DEPENDENT_QUERIES,
];
